import React, { useState, useEffect, useRef, RefObject } from 'react';
import { PDFDocument } from 'pdf-lib';
import { ActivityIndicator, Platform, View } from 'react-native';
import ReactNativeBlobUtil from 'react-native-blob-util';
import {
  GestureHandlerRootView,
  PanGestureHandler,
  PanGestureHandlerGestureEvent,
  State,
} from 'react-native-gesture-handler';
import Pdf from 'react-native-pdf';
import { Pagination } from 'react-native-snap-carousel';
import { WebView } from 'react-native-webview';
import { useTheme } from 'styled-components/native';
import { trackError } from '~/utils/sentry';
import {
  PreviewWrapperContainer,
  PaginationContainer,
  PreviewWrapper,
  WebPreviewContainer,
  WebPreviewText,
} from './style';

const PDFViewer = ({
  uri,
  hidePreview = false,
  height = 310,
  width = 220,
  name,
}: {
  uri: string;
  hidePreview?: boolean;
  width?: number;
  height?: number;
  name?: string | null;
}) => {
  const theme = useTheme();
  const [pageCount, setPageCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Temporary workaround for iOS pdf preview
  const [base64Data, setBase64Data] = useState<string | null>(null);
  const [isLoadingBase64, setIsLoadingBase64] = useState(false);
  const pdfRef = useRef<Pdf>(null);

  useEffect(() => {
    const fetchPageCount = async () => {
      try {
        // For local file:// URIs, fetch() can't access them
        // Set a default page count - react-native-pdf will update it via onLoadComplete on Android
        if (uri.startsWith('file://')) {
          setPageCount(1);
          return;
        }

        const response = await fetch(uri);
        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        setPageCount(pdfDoc.getPageCount());
      } catch (e) {
        // Set default page count so PDF still renders
        setPageCount(1);
        trackError(e);
      }
    };

    fetchPageCount();
  }, [uri]);

  // Temporary workaround for iOS pdf preview
  // For iOS local files, convert to base64 for WebView rendering
  useEffect(() => {
    const loadBase64ForLocalFile = async () => {
      if (Platform.OS === 'ios' && uri.startsWith('file://')) {
        setIsLoadingBase64(true);
        try {
          // Remove file:// prefix and decode URI for file path
          const filePath = decodeURIComponent(uri.replace('file://', ''));
          const base64 = await ReactNativeBlobUtil.fs.readFile(
            filePath,
            'base64',
          );
          setBase64Data(base64);
        } finally {
          setIsLoadingBase64(false);
        }
      }
    };

    loadBase64ForLocalFile();
  }, [uri]);

  const handlePageChange = (page: number) => {
    try {
      setCurrentPage(page);
      if (pdfRef.current && page !== currentPage) {
        pdfRef.current.setPage(page);
      }
    } catch (e) {
      trackError(e);
    }
  };

  const handleGesture = ({ nativeEvent }: PanGestureHandlerGestureEvent) => {
    const { state, translationX } = nativeEvent;
    const isEndOrFailed = state === State.END || state === State.FAILED;
    const isSwipeLeft = translationX < 0 && currentPage < pageCount;
    const isSwipeRight = translationX > 0 && currentPage > 1;

    if (!isEndOrFailed) return;

    let changePage = 0;
    if (isSwipeLeft) {
      changePage = 1;
    } else if (isSwipeRight) {
      changePage = -1;
    }

    handlePageChange(currentPage + changePage);
  };

  if (Platform.OS === 'web') {
    return (
      <WebPreviewContainer
        style={{
          height,
          width,
        }}
      >
        <WebPreviewText>
          {'PDF viewer not available on web ' + (name ?? '')}
        </WebPreviewText>
      </WebPreviewContainer>
    );
  }

  if (hidePreview) {
    // Temporary workaround for iOS pdf preview problem
    // Only decode local file:// URIs on iOS for special characters in filenames
    // Do NOT decode remote URLs as they have encoded query params that must stay encoded
    const isLocalFile = uri.startsWith('file://');
    const sourceUri =
      Platform.OS === 'ios' && isLocalFile ? decodeURIComponent(uri) : uri;

    // Use WebView for iOS as react-native-pdf has compatibility issues with Expo SDK 54
    if (Platform.OS === 'ios') {
      // For local files, use base64 data URI since WebView can't access file:// directly
      if (isLocalFile) {
        if (isLoadingBase64) {
          return (
            <View
              style={{
                width,
                height,
                backgroundColor: '#f0f0f0',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ActivityIndicator size="small" />
            </View>
          );
        }

        if (base64Data) {
          const html = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
                <style>
                  * { margin: 0; padding: 0; }
                  html, body { width: 100%; height: 100%; overflow: hidden; }
                  embed { width: 100%; height: 100%; }
                </style>
              </head>
              <body>
                <embed src="data:application/pdf;base64,${base64Data}" type="application/pdf" />
              </body>
            </html>
          `;
          return (
            <View
              style={{
                width,
                height,
                backgroundColor: '#f0f0f0',
                overflow: 'hidden',
              }}
            >
              <WebView
                source={{ html }}
                style={{ flex: 1, width, height }}
                onLoadStart={() => {}}
                onLoadEnd={() => {}}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onError={(e) => {}}
                scrollEnabled={false}
                bounces={false}
              />
            </View>
          );
        }

        // Fallback while loading
        return (
          <View
            style={{
              width,
              height,
              backgroundColor: '#f0f0f0',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ActivityIndicator size="small" />
          </View>
        );
      }

      // For remote URLs, WebView can load directly
      return (
        <View
          style={{
            width,
            height,
            backgroundColor: '#f0f0f0',
            overflow: 'hidden',
          }}
        >
          <WebView
            source={{ uri: sourceUri }}
            style={{ flex: 1, width, height }}
            onLoadStart={() => {}}
            onLoadEnd={() => {}}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onError={(e) => {}}
            scalesPageToFit={true}
            scrollEnabled={false}
            bounces={false}
            startInLoadingState={true}
            renderLoading={() => (
              <View
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                }}
              >
                <ActivityIndicator size="small" />
              </View>
            )}
          />
        </View>
      );
    }

    // Use react-native-pdf for Android
    return (
      <View style={{ width, height, backgroundColor: '#f0f0f0' }}>
        <Pdf
          ref={pdfRef as RefObject<Pdf>}
          source={{ uri: sourceUri, cache: false }}
          trustAllCerts={false}
          singlePage
          onPageChanged={(page) => {
            handlePageChange(page);
          }}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onLoadComplete={(numberOfPages, filePath) => {
            setPageCount(numberOfPages);
          }}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onError={(error) => {}}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onLoadProgress={(percent) => {}}
          fitPolicy={0}
          enablePaging
          style={{ flex: 1, width, height, backgroundColor: 'white' }}
        />
      </View>
    );
  }

  // Temporary workaround for iOS pdf preview problem
  // Only decode local file:// URIs on iOS for special characters in filenames
  const isLocalFile = uri.startsWith('file://');
  const pdfSourceUri =
    Platform.OS === 'ios' && isLocalFile ? decodeURIComponent(uri) : uri;

  return (
    <GestureHandlerRootView>
      <PanGestureHandler onHandlerStateChange={handleGesture}>
        <PreviewWrapperContainer>
          <PreviewWrapper>
            <Pdf
              ref={pdfRef as RefObject<Pdf>}
              source={{ uri: pdfSourceUri, cache: true }}
              trustAllCerts={false}
              singlePage
              onPageChanged={(page) => handlePageChange(page)}
              onLoadComplete={(numberOfPages) => {
                setPageCount(numberOfPages);
              }}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onError={(error) => {}}
              fitPolicy={0}
              enablePaging
              style={{ flex: 1, width, height }}
            />
            {currentPage < pageCount && (
              <View
                style={{
                  overflow: 'hidden',
                  position: 'absolute',
                  right: -83,
                  opacity: 0.5,
                }}
              >
                <Pdf
                  style={{ width, height }}
                  source={{ uri: pdfSourceUri }}
                  page={currentPage + 1}
                  singlePage={true}
                  fitPolicy={0}
                  enablePaging={true}
                />
              </View>
            )}
          </PreviewWrapper>
          <PaginationContainer>
            <Pagination
              dotsLength={pageCount}
              activeDotIndex={currentPage - 1}
              containerStyle={{
                backgroundColor: theme?.color.base.c2,
                paddingHorizontal: 5,
                paddingVertical: 5,
                borderRadius: 10,
              }}
              dotContainerStyle={{
                marginHorizontal: 2,
              }}
              dotStyle={{
                height: 6,
                width: 6,
                borderRadius: 5,
                backgroundColor: theme?.color.base.c6,
              }}
              inactiveDotStyle={{
                height: 6,
                width: 6,
                borderRadius: 5,
                backgroundColor: theme?.color.base.c4,
              }}
              inactiveDotOpacity={1.0}
              inactiveDotScale={1.0}
              tappableDots
            />
          </PaginationContainer>
        </PreviewWrapperContainer>
      </PanGestureHandler>
    </GestureHandlerRootView>
  );
};

export default PDFViewer;
